import React, { useMemo, useCallback, useState } from 'react';
import { Box, Typography, Button, Badge, Table } from '@lucid/lego';
import type { TableColumn } from '@lucid/lego';
import type { ModuleFileReference } from '../../../../../../api/services/lupp/order/dto.order';
import {
  downloadSfdcFileFromFilehub,
  fetchSfdcFileBlob,
} from '../../../../../../api/services/lupp/fileUpload/fileUpload';
import { formatDate } from '../../../../../../utils/tools';
import styles from './InvoicesTable.module.scss';

type Invoice = {
  invoiceNumber: string;
  generatedDate: string;
  id: string;
  orderId: string;
  sfdcFileId: string;
  status: string;
  isVoided: boolean;
};

export type InvoicesTableProps = {
  invoicesData: ModuleFileReference[];
  sfdcDeliveryId: string;
  theme: 'light' | 'dark';
  labels: Record<string, string>;
  dataTestId?: string;
};

/**
 * Renders a file references table with download and view actions.
 * Used for final invoices and delivery acceptance documents.
 * Download is enabled when a row has an SFDC file identifier.
 *
 * @component
 */
const InvoicesTable: React.FC<InvoicesTableProps> = ({
  invoicesData,
  sfdcDeliveryId,
  theme,
  labels,
  dataTestId = 'invoices-table',
}) => {
  const invertedTheme = theme === 'dark' ? 'light' : 'dark';

  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const invoices = useMemo<Invoice[]>(() => {
    if (!invoicesData?.length) return [];

    return [...invoicesData]
      .map(
        ({
          partnerPortalDocumentIdentifier,
          fileIdentifier,
          sfdcFileIdentifier,
          moduleReferenceIdentifier,
          fileNameOriginal,
          createdDatetime,
          fileGenerationStatus,
        }: ModuleFileReference) => ({
          id: partnerPortalDocumentIdentifier || fileIdentifier || '',
          orderId: moduleReferenceIdentifier || '',
          sfdcFileId: sfdcFileIdentifier || '',
          invoiceNumber: fileNameOriginal || sfdcFileIdentifier || '',
          generatedDate: createdDatetime || '',
          status: fileGenerationStatus || '',
          isVoided: fileGenerationStatus === 'VOID',
        })
      )
      .sort((a, b) => new Date(b.generatedDate).getTime() - new Date(a.generatedDate).getTime());
  }, [invoicesData]);

  const handleDownload = useCallback(
    async (invoice: Invoice) => {
      setDownloadingId(invoice.sfdcFileId);
      try {
        await downloadSfdcFileFromFilehub(
          sfdcDeliveryId,
          invoice.sfdcFileId,
          invoice.invoiceNumber
        );
      } catch {
        // Error handled silently; consider showing a toast in future
      } finally {
        setDownloadingId(null);
      }
    },
    [sfdcDeliveryId]
  );

  const handleOpenInvoice = useCallback(
    async (invoice: Invoice) => {
      try {
        const { blob } = await fetchSfdcFileBlob(sfdcDeliveryId, invoice.sfdcFileId);
        // Ensure the blob has the correct MIME type for PDF
        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(pdfBlob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      } catch {
        // Error handled silently
      }
    },
    [sfdcDeliveryId]
  );

  const columns = useMemo<TableColumn<Invoice>[]>(
    () => [
      {
        key: 'invoiceNumber',
        title: labels?.invoiceColumnNumber || 'Invoice #',
        width: '30%',
        sortable: true,
        render: (_value, invoice) => (
          <Typography
            variant="body"
            size="small"
            color={invoice.isVoided || !invoice.sfdcFileId ? 'black' : 'link'}
            theme={theme}
            onClick={() => !invoice.isVoided && invoice.sfdcFileId && handleOpenInvoice(invoice)}
            className={
              invoice.isVoided || !invoice.sfdcFileId
                ? styles.invoiceLinkDisabled
                : styles.invoiceLink
            }
          >
            {invoice.invoiceNumber}
          </Typography>
        ),
      },
      {
        key: 'status',
        title: labels?.invoiceColumnStatus || 'Status',
        width: '30%',
        sortable: true,
        render: (_value, invoice) => (
          <Box className={styles.statusCell}>
            <Typography variant="body" size="small" color="black" theme={theme}>
              {invoice.status === 'GENERATING'
                ? labels?.generatingStatus || 'Generating'
                : invoice.status === 'GENERATED'
                  ? labels?.generatedStatus || 'Generated'
                  : invoice.status === 'VOID'
                    ? labels?.voidedStatus || 'Voided'
                    : invoice.status}
            </Typography>
            {invoice.isVoided && (
              <Badge
                label={labels?.voidLabel || 'VOID'}
                color="error"
                type="secondary"
                theme={theme}
              />
            )}
          </Box>
        ),
      },
      {
        key: 'generatedDate',
        title: labels?.invoiceColumnDate || 'Generated Date',
        width: '25%',
        sortable: true,
        render: (_value, invoice) => (
          <Typography variant="body" size="small" color="black" theme={theme}>
            {formatDate(invoice.generatedDate)}
          </Typography>
        ),
      },
      {
        key: 'action',
        title: labels?.invoiceColumnAction || 'Action',
        width: '15%',
        sortable: false,
        render: (_value, invoice) =>
          !invoice.isVoided && invoice.sfdcFileId ? (
            <Button
              label={
                downloadingId === invoice.sfdcFileId
                  ? labels?.downloadingButton || 'Downloading...'
                  : labels?.downloadButton || 'Download'
              }
              size="small"
              color="neutral"
              type="secondary"
              theme={invertedTheme}
              onClick={() => handleDownload(invoice)}
              gaComponent="download-invoice"
              dataTestId={`download-invoice-${invoice.sfdcFileId}`}
              disabled={!!downloadingId}
              className={styles.downloadButton}
            />
          ) : null,
      },
    ],
    [handleDownload, handleOpenInvoice, labels, theme, invertedTheme, downloadingId]
  );

  if (!invoices.length) return null;

  return (
    <Table
      columns={columns}
      data={invoices}
      theme={invertedTheme}
      selection={{ enabled: false }}
      pagination={{ enabled: false }}
      emptyState={{ message: labels?.noInvoices || 'No invoices available' }}
      gaComponent="invoices-table"
      dataTestId={dataTestId}
      compact
    />
  );
};

InvoicesTable.displayName = 'InvoicesTable';

export default InvoicesTable;
